import { dateFns, dateTimeFns, Iso } from 'iso-fns2'
import { isoZ, z } from 'my-zod'
import { VehicleMetricSchemas } from '../../../../polaris-adventures-js'
import { getVehicleMetrics } from '../../fleet-operations/vehicle-metric'
import { getTypeFromAssetFamilyId } from '../../fleet-operations/asset-family'
import { enumOptionsToObject, makeValidator } from '../../internal'

const vehicleUseHistoryModel = z.object({
  id: z.string().uuid(),
  operatorId: z.number(),
  vehicleId: z.number(),

  prep: z
    .object({
      engineHours: z.number().nullable(),
      mileage: z.number().int().nullable(),
      treadDepth32ndInches: z.number().int().nullable(),
      conditionNotes: z.string(),
      fuelLevel: z.string().nullable(),
      vehicleImages: z.string().array()
    })
    .nullable(),

  checkOut: z
    .object({
      driverRiderId: z.string().uuid(),
      reservationVehicleId: z.string().uuid(),
      date: isoZ.date(),
      time: isoZ.time(),
      formSnapshotUrl: z.string(),
      engineHours: z.number().nullable(),
      mileage: z.number().int().nullable(),
      treadDepth32ndInches: z.number().int().nullable(),
      fuelLevel: z.string().nullable(),
      conditionNotes: z.string(),
      vehicleImages: z.string().array()
    })
    .nullable(),

  checkIn: z
    .object({
      date: isoZ.date(),
      time: isoZ.time(),
      formSnapshotUrl: z.string(),
      engineHours: z.number().nullable(),
      mileage: z.number().int().nullable(),
      treadDepth32ndInches: z.number().int().nullable(),
      fuelLevel: z.string().nullable(),
      conditionNotes: z.string(),
      vehicleImages: z.string().array(),
      didCustomerHitAnything: z.boolean().nullable(),
      didVehicleHaveMechanicalOrElectricalIssue: z.boolean().nullable(),
      customerMachineRanOk: z.boolean().nullable()
    })
    .nullable(),

  createdDate: isoZ.instant(),
  lastModifiedDate: isoZ.instant(),
  deletedDate: isoZ.instant().nullable()
})

const vehicleUseHistorySystemView = vehicleUseHistoryModel

const vehicleUseHistoryPrep = vehicleUseHistoryModel.shape.prep.unwrap().augment({
  operatorId: z.number(),
  vehicleId: z.number(),
  engineHours: z.number().nonnegative('Must be a positive number').nullable(),
  mileage: z.number().int().nonnegative('Must be a positive number').nullable()
})

const vehicleUseHistoryCheckOut = vehicleUseHistoryModel.shape.checkOut
  .unwrap()
  .omit({ formSnapshotUrl: true, reservationVehicleId: true })
  .augment({
    reservationId: z.string(),
    operatorId: z.number(),
    vehicleId: z.number(),
    driverSignatureBase64: z.string().min(1, 'Required'),
    staffSignatureBase64: z.string().min(1, 'Required'),
    engineHours: z.number().nonnegative('Must be a positive number').nullable(),
    mileage: z.number().int().nonnegative('Must be a positive number').nullable(),
    rentalLength: z.number().int().nonnegative('Must be a positive number').nullable(),
    otherRiderIds: z.string().array()
  })

const vehicleUseHistoryCheckIn = vehicleUseHistoryModel.shape.checkIn
  .unwrap()
  .omit({ formSnapshotUrl: true })
  .augment({
    id: z.string().uuid(),
    operatorId: z.number(),
    vehicleId: z.number(),
    driverSignatureBase64: z.string().min(1, 'Required'),
    staffSignatureBase64: z.string().min(1, 'Required'),
    engineHours: z.number().nonnegative('Must be a positive number').nullable(),
    mileage: z.number().int().nonnegative('Must be a positive number').nullable()
  })

function getRentalLengthHours(input: {
  beginDate: Iso.Date
  endDate: Iso.Date
  ridingStartTime: Iso.Time
  ridingEndTime: Iso.Time
}) {
  const startDateTime = dateFns.toDateTime(input.beginDate, input.ridingStartTime)
  const endDateTime = dateFns.toDateTime(input.endDate, input.ridingEndTime)
  const rentalLengthHours = dateTimeFns
    .chain(startDateTime)
    .since(endDateTime, { largestUnit: 'hours', smallestUnit: 'hours' })
    .abs()
    .getHours()
    .value()
  return rentalLengthHours
}

function IsVehiclePrepped(
  history: Pick<VehicleUseHistoryV2Schemas.vehicleUseHistorySystemView, 'vehicleId' | 'lastModifiedDate'> & {
    prep: {} | null
  },
  vehiclePrepExpirationDays: number
) {
  /**
   * If the returned record does not have a checkoutDateTime or a checkInDateTime,
   * but has information such as hours, miles, or other relevant data, and was modified
   * within the vehiclePrepExpirationDays, then it can be identified as a preparation record
   */

  if (history.prep && history.lastModifiedDate) {
    const sinceLastModified = dateTimeFns.chain(dateTimeFns.now()).since(dateTimeFns.from(history.lastModifiedDate))
    const daysSinceLastModified = sinceLastModified.getDays().value()
    const remainingHoursSinceLastModified = sinceLastModified.getHours().value()
    const totalHoursSinceLastModified = daysSinceLastModified * 24 + remainingHoursSinceLastModified
    return totalHoursSinceLastModified < vehiclePrepExpirationDays * 24
  }

  return false
}

function getVehicleUseHistoryFormDefaultValues(
  lastVehicleUseHistory: VehicleUseHistoryV2Schemas.vehicleUseHistorySystemView | null,
  metrics: VehicleMetricSchemas.vehicleMetricModel | null,
  vehiclePrepExpirationDays: number
) {
  const { prep } =
    !!lastVehicleUseHistory && IsVehiclePrepped(lastVehicleUseHistory, vehiclePrepExpirationDays)
      ? lastVehicleUseHistory
      : { prep: null }
  return {
    engineHours: metrics?.engineHours ?? prep?.engineHours,
    mileage: metrics?.mileage ?? prep?.mileage,
    treadDepth32ndInches:
      metrics?.treadDepth ?? prep?.treadDepth32ndInches ?? lastVehicleUseHistory?.checkIn?.treadDepth32ndInches,
    fuelLevel: prep?.fuelLevel ?? lastVehicleUseHistory?.checkIn?.fuelLevel,
    conditionNotes: prep?.conditionNotes ?? '',
    engineHoursHint: metrics?.engineHours ?? prep?.engineHours ?? lastVehicleUseHistory?.checkIn?.engineHours,
    mileageHint: metrics?.mileage ?? prep?.mileage ?? lastVehicleUseHistory?.checkIn?.mileage,
    mileageIsFromTelemetry: metrics ? VehicleMetricSchemas.isMetricFromTelemetry(metrics, 'mileage') : false,
    engineHoursIsFromTelemetry: metrics ? VehicleMetricSchemas.isMetricFromTelemetry(metrics, 'engineHours') : false
  }
}

function getLastVehiclePrepMetrics(
  lastVehicleUseHistory: VehicleUseHistoryV2Schemas.vehicleUseHistorySystemView | null,
  metrics: VehicleMetricSchemas.vehicleMetricModel | null,
  vehiclePrepExpirationDays: number
) {
  const { prep } =
    !!lastVehicleUseHistory && IsVehiclePrepped(lastVehicleUseHistory, vehiclePrepExpirationDays)
      ? lastVehicleUseHistory
      : { prep: null }
  return lastVehicleUseHistory
    ? {
        ...metrics,
        engineHours: metrics?.engineHours ?? prep?.engineHours ?? lastVehicleUseHistory.checkIn?.engineHours,
        mileage: metrics?.mileage ?? prep?.mileage ?? lastVehicleUseHistory.checkIn?.mileage,
        treadDepth: metrics?.treadDepth ?? prep?.treadDepth32ndInches ?? lastVehicleUseHistory.checkIn?.treadDepth32ndInches
      }
    : metrics
}

const validateMetricsForVehicleUseHistory = makeValidator<{
  vehicleUseHistory: {
    fuelLevel: string | null
    engineHours: number | null
    mileage: number | null
    treadDepth32ndInches: number | null
    vehicleImages: string[]
  }
  assetFamilyId: string
  productLine: string | null
}>(({ assetFamilyId, productLine, vehicleUseHistory }, error) => {
  const relevantMetrics = getVehicleMetrics({
    assetFamilyType: getTypeFromAssetFamilyId(assetFamilyId),
    productLine: productLine
  })
  if (relevantMetrics.includes('fuel') && !vehicleUseHistory.fuelLevel) {
    error('Required', ['fuelLevel'])
  }
  if (relevantMetrics.includes('hours') && !vehicleUseHistory.engineHours) {
    error('Required', ['engineHours'])
  }
  if (relevantMetrics.includes('mileage') && !vehicleUseHistory.mileage) {
    error('Required', ['mileage'])
  }
  if (relevantMetrics.includes('treadDepth32ndInches') && !vehicleUseHistory.treadDepth32ndInches) {
    error('Required', ['treadDepth32ndInches'])
  }
  if (!vehicleUseHistory.vehicleImages.length) {
    error('Required', ['vehicleImages'])
  } else if (vehicleUseHistory.vehicleImages.length < 4) {
    error('Minimum of 4 images required', ['vehicleImages'])
  }
})

const vehicleCheckOutErrorTypeOptions = [
  'all-reservation-vehicles-checked-out',
  'riders-cannot-be-associated',
  'vehicle-checked-out',
  'error-in-vehicle-transfer-process',
  'error-in-check-out-process',
  'unavailable-driver',
  'unavailable-reservation',
  'unavailable-driver-and-reservation',
  'unavailable-vehicle',
  'unavailable-riders',
  'unavailable-vehicle-and-riders'
] as const
const vehicleCheckOutErrorTypes = enumOptionsToObject(vehicleCheckOutErrorTypeOptions)
type VehicleCheckOutErrorType = (typeof vehicleCheckOutErrorTypeOptions)[number]
const vehicleCheckOutErrorMessages: Record<VehicleCheckOutErrorType, string> = {
  'all-reservation-vehicles-checked-out': 'All vehicles in the selected reservation were checked-out',
  'riders-cannot-be-associated': 'Some selected riders have been removed or attached to a different reservation',
  'vehicle-checked-out': 'The vehicle has already been checked-out',
  'error-in-vehicle-transfer-process':
    'Something went wrong during the transfer vehicle process, please check selected vehicle',
  'error-in-check-out-process': 'Something went wrong during the check-out process',
  'unavailable-driver': 'The selected driver is no longer available',
  'unavailable-reservation': 'The selected reservation is no longer available',
  'unavailable-driver-and-reservation': 'The selected driver and reservation are no longer available',
  'unavailable-vehicle': 'The selected vehicle is no longer available',
  'unavailable-riders': 'One or more of the selected riders are no longer available',
  'unavailable-vehicle-and-riders': 'The selected vehicle and one or more of the selected riders are no longer available'
}

export const VehicleUseHistoryV2Schemas = {
  vehicleUseHistoryModel,
  vehicleUseHistorySystemView,
  vehicleUseHistoryPrep,
  vehicleUseHistoryCheckOut,
  vehicleUseHistoryCheckIn,
  getRentalLengthHours,
  IsVehiclePrepped,
  getVehicleUseHistoryFormDefaultValues,
  getLastVehiclePrepMetrics,
  validateMetricsForVehicleUseHistory,
  vehicleCheckOutErrorTypes,
  vehicleCheckOutErrorMessages
}

export declare namespace VehicleUseHistoryV2Schemas {
  export type vehicleUseHistorySystemView = z.infer<typeof vehicleUseHistorySystemView>
  export type vehicleUseHistoryPrep = z.infer<typeof vehicleUseHistoryPrep>
  export type vehicleUseHistoryCheckOut = z.infer<typeof vehicleUseHistoryCheckOut>
  export type vehicleUseHistoryCheckIn = z.infer<typeof vehicleUseHistoryCheckIn>
}
